import { length } from "@turf/turf"
import jobService from '@/services/jobService'

const RESTAPI_BASE = import.meta.env.VITE_APP_RESTAPI || "https://demo.routaapi.com/"
const ORDER_URL = `${RESTAPI_BASE}order`
const ROUTE_OPTIMIZATION_JOB_URL = `${RESTAPI_BASE}route-optimization/job`
const ROUTE_MASK_URL = `${RESTAPI_BASE}route-optimization/mask`

let maskAbortController = null

const getDefaultState = () => ({
    routeOptimizationResult: null,
    originalRouteGeometry: null,
    routeMaskGeometry: null,
    routeMaskLoading: false,
    routeMaskError: null,
    usedVehicles: [],
    selectedContract: null,
    selectedOrder: null,
    vehicles: [],
    singleVehicle: null,
    selectedGeometry: null,
    loadedGeometries: [],
    geometrySource: null,
    showValidation: false,
    showSearchTripsModal: false,
    contractsLoading: false,
    ordersLoading: false,
    availableContracts: [],
    availableOrders: [],
    optimizerLoading: false,
    navigationContractId: null,
    navigationOrderId: null,
    maxDuration: null,
    maxDurationMinutes: null,
    maxDurationHours: null,
    optimizationName: null,
    resubmitJobId: null,
    operationType: 'winter_maintenance',
})

const state = getDefaultState()

// Route type: 1 = road, 2 = sidewalk/cycle path, 3 = both
const DEFAULT_ROUTE_TYPE = 1

// Colors matching the route type badge styles in RouteOptimizationSetup.vue
const ROUTE_TYPE_COLORS = {
    1: '#0d6efd', // road — blue
    2: '#7FC000', // sidewalk — green
    3: '#00C896', // both — teal
}

const normalizeGeometryItems = (items = [], fallbackLabel = 'Route') =>
    items.map((item, index) => {
        const hasValidGeometry = item.geometry?.features?.some(feature =>
            feature.geometry?.type === 'LineString' || feature.geometry?.type === 'MultiLineString'
        )
        const routeType = item.route_type ?? DEFAULT_ROUTE_TYPE
        return {
            ...item,
            geometry: typeof item.geometry === "string" ? JSON.parse(item.geometry) : item.geometry,
            selected: hasValidGeometry,
            disabled: !hasValidGeometry,
            route_type: routeType,
            name: item.name || item.description || item.display_name || `${fallbackLabel} ${index + 1}`,
            color: ROUTE_TYPE_COLORS[routeType] || ROUTE_TYPE_COLORS[DEFAULT_ROUTE_TYPE],
        }
    })

const validateLoadedGeometries = (loadedGeometries) => {
    const selected = loadedGeometries.filter((item) => item.selected)
    if (!selected.length) return false

    return selected.some((item) => {
        if (!item.geometry?.features?.length) return false

        return item.geometry.features.some(feature => {
            if (!feature.geometry) return false

            const isLineString = feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiLineString'
            if (!isLineString) return false

            if (feature.geometry.type === 'LineString' && feature.geometry.coordinates?.length < 2) return false
            if (feature.geometry.type === 'MultiLineString' && !feature.geometry.coordinates?.some(line => line.length >= 2)) return false

            try {
                return length(feature, { units: "kilometers" }) > 0
            } catch {
                return false
            }
        })
    })
}

const getters = {
    canOptimize(state) {
        const hasValidGeometry = validateLoadedGeometries(state.loadedGeometries)
        const isRoadInspection = state.operationType === 'road_inspection'

        const isMaxDurationMode = (state.maxDurationHours > 0 || state.maxDurationMinutes > 0)

        const vehiclesToValidate = isMaxDurationMode
            ? (state.singleVehicle ? [state.singleVehicle] : [])
            : state.vehicles

        const hasValidVehicles = vehiclesToValidate.length > 0 && vehiclesToValidate.every(v => {
            if (!v.properties?.max_speed || v.properties.max_speed <= 0 || v.properties.max_speed >= 200) return false
            if (isRoadInspection) return true
            return v.properties?.work_width > 0 &&
                   v.properties.work_width < 1500 &&
                   v.properties?.work_speed > 0 &&
                   v.properties.work_speed < 200
        })

        return hasValidGeometry && hasValidVehicles
    },
    allSelected(state) {
        return state.loadedGeometries.length > 0 && state.loadedGeometries.every((g) => g.selected)
    },
    totalGeometryKilometers(state) {
        let totalKm = 0
        state.loadedGeometries
            .filter((g) => g.selected)
            .forEach((item) => {
                item.geometry?.features?.forEach((feature) => {
                    if (
                        feature.geometry &&
                        (feature.geometry.type === "LineString" || feature.geometry.type === "MultiLineString")
                    ) {
                        totalKm += length(feature, { units: "kilometers" })
                    }
                })
            })
        return `${totalKm.toFixed(2)} km`
    },
}

const mutations = {
    storeRouteOptimizationResult(state, value) {
        state.routeOptimizationResult = value
    },
    storeUsedVehicles(state, value) {
        state.usedVehicles = value
    },
    removeUsedVehicle(state, index) {
        state.usedVehicles.splice(index, 1)
    },
    resetUsedVehicles(state) {
        state.usedVehicles = []
    },
    // Reset the state to its initial state
    resetRouteOptimization(state) {
        // Clear locationManuallySet from existing vehicles before reset
        if (state.vehicles) {
            state.vehicles.forEach(vehicle => {
                if (vehicle.properties) {
                    delete vehicle.properties.locationManuallySet;
                }
            });
        }
        if (state.singleVehicle?.properties) {
            delete state.singleVehicle.properties.locationManuallySet;
        }
        Object.assign(state, getDefaultState())
    },
    removeFromUsedVehicles(state, index) {
        if (index >= 0 && index < state.usedVehicles.length) {
            state.usedVehicles.splice(index, 1)
        }
    },
    resetVehicles(state) {
        state.usedVehicles = []
    },
    setSelectedContract(state, contract) {
        state.selectedContract = contract
    },
    setSelectedOrder(state, order) {
        state.selectedOrder = order
    },
    setVehicles(state, vehicles) {
        state.vehicles = vehicles
    },
    setSingleVehicle(state, vehicle) {
        state.singleVehicle = vehicle
    },
    setSelectedGeometry(state, geometry) {
        state.selectedGeometry = geometry
    },
    setLoadedGeometries(state, geometries) {
        state.loadedGeometries = geometries
    },
    setGeometrySource(state, source) {
        state.geometrySource = source
    },
    setShowValidation(state, value) {
        state.showValidation = value
    },
    setShowSearchTripsModal(state, value) {
        state.showSearchTripsModal = value
    },
    setContractsLoading(state, value) {
        state.contractsLoading = value
    },
    setOrdersLoading(state, value) {
        state.ordersLoading = value
    },
    setAvailableContracts(state, contracts) {
        state.availableContracts = contracts
    },
    setAvailableOrders(state, orders) {
        state.availableOrders = orders
    },
    setOptimizerLoading(state, value) {
        state.optimizerLoading = value
    },
    setGeometrySelection(state, { index, value }) {
        const item = state.loadedGeometries[index]
        if (!item) return
        item.selected = value
    },
    setGeometryRouteType(state, { index, value }) {
        const item = state.loadedGeometries[index]
        if (!item) return
        item.route_type = value
        item.color = ROUTE_TYPE_COLORS[value] || ROUTE_TYPE_COLORS[DEFAULT_ROUTE_TYPE]
    },
    setAllGeometriesRouteType(state, value) {
        state.loadedGeometries = state.loadedGeometries.map((item) => ({
            ...item,
            route_type: value,
            color: ROUTE_TYPE_COLORS[value] || ROUTE_TYPE_COLORS[DEFAULT_ROUTE_TYPE],
        }))
    },
    setAllGeometriesSelection(state, value) {
        state.loadedGeometries = state.loadedGeometries.map((item) => ({
            ...item,
            selected: value,
        }))
    },
    setNavigationData(state, { contractId, orderId }) {
        state.navigationContractId = contractId
        state.navigationOrderId = orderId
    },
    setMaxDurationMinutes(state, value) {
        state.maxDurationMinutes = value
    },
    setMaxDurationHours(state, value) {
        state.maxDurationHours = value
    },
    setMaxDuration(state, value) {
        state.maxDuration = value
    },
    setOptimizationName(state, value) {
        state.optimizationName = value
    },
    setResubmitJobId(state, value) {
        state.resubmitJobId = value
    },
    setOperationType(state, value) {
        state.operationType = value || 'winter_maintenance'
    },
    setOriginalRouteGeometry(state, geometry) {
        state.originalRouteGeometry = geometry
    },
    setRouteMaskGeometry(state, geometry) {
        state.routeMaskGeometry = geometry
    },
    setRouteMaskLoading(state, value) {
        state.routeMaskLoading = value
    },
    setRouteMaskError(state, value) {
        state.routeMaskError = value
    },
}

const actions = {
    selectContract({ commit }, contract) {
        commit("setSelectedContract", contract)
    },
    async selectOrder({ commit, dispatch }, { order, routeLabel } = {}) {
        if (!order) {
            commit("setSelectedOrder", null)
            commit("setSelectedGeometry", null)
            return
        }
        commit("setSelectedOrder", order)
        await dispatch("fetchOrderGeometry", { orderId: order.id, routeLabel })
    },
    async fetchOrderGeometry({ commit, dispatch, rootState }, { orderId, routeLabel } = {}) {
        if (!orderId) return
        commit("setOrdersLoading", true)
        try {
            const response = await fetch(`${ORDER_URL}/${orderId}/geometry`, {
                headers: {
                    Authorization: rootState.token,
                },
            })
            const body = await response.json().catch(() => null)
            if (response.ok && body) {
                const payload = Array.isArray(body.data) ? body.data : Array.isArray(body) ? body : null
                if (payload) {
                    const prepared = normalizeGeometryItems(payload, routeLabel)
                    commit("setLoadedGeometries", prepared)
                    commit("setGeometrySource", "order")
                    dispatch("updateSelectedGeometry")
                }
            }
        } catch (err) {
            console.error("Failed to fetch order geometry", err)
        } finally {
            commit("setOrdersLoading", false)
        }
    },
    async loadGeometryFromFile({ commit, dispatch }, file) {
        if (!file) return
        try {
            const text = await file.text()
            const geojson = JSON.parse(text)
            const baseName = file.name.replace(/\.[^.]+$/, '')
            const firstFeature = geojson.features?.[0]

            const items = [{
                id: 'file-0',
                name: firstFeature?.properties?.name || firstFeature?.properties?.nimi || baseName,
                geometry: geojson,
                selected: true,
                disabled: false,
                route_type: firstFeature?.properties?.route_type ?? DEFAULT_ROUTE_TYPE,
                color: ROUTE_TYPE_COLORS[firstFeature?.properties?.route_type ?? DEFAULT_ROUTE_TYPE] || ROUTE_TYPE_COLORS[DEFAULT_ROUTE_TYPE],
                line_width: 2,
            }]

            commit("setLoadedGeometries", items)
            commit("setGeometrySource", "file")
            dispatch("updateSelectedGeometry")
        } catch (error) {
            console.error("Invalid GeoJSON file", error)
        }
    },
    loadGeometriesFromTrips({ commit, dispatch }, tripsWithGeometry) {
        if (!Array.isArray(tripsWithGeometry)) return
        const prepared = tripsWithGeometry.map((trip) => ({
            id: trip.id,
            name: `${trip.id}`,
            description: `${trip.vehicle || "Vehicle"} - ${trip.start_time} to ${trip.end_time}`,
            geometry: {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        geometry: {
                            type: "LineString",
                            coordinates: trip.geometry.map((point) => [point.x, point.y]),
                        },
                        properties: {},
                    },
                ],
            },
            selected: true,
            route_type: DEFAULT_ROUTE_TYPE,
            color: ROUTE_TYPE_COLORS[DEFAULT_ROUTE_TYPE],
            line_width: 3,
        }))

        commit("setLoadedGeometries", prepared)
        commit("setGeometrySource", "trips")
        dispatch("updateSelectedGeometry")
    },
    updateSelectedGeometry({ dispatch }) {
        // Triggered whenever geometry selection or route type changes — re-fetches the route mask
        dispatch('fetchRouteMask')
    },
    async fetchRouteMask({ commit, state, rootState }) {
        // Cancel any in-flight request so only the latest selection triggers a mask
        if (maskAbortController) maskAbortController.abort()
        maskAbortController = new AbortController()

        // Capture the controller for this invocation so the finally block can
        // guard against clearing loading state that belongs to a newer request
        const thisController = maskAbortController

        // Collect only LineString/MultiLineString features from selected geometry items
        const selectedFeatures = state.loadedGeometries
            .filter((item) => item.selected)
            .flatMap((item) =>
                (item.geometry?.features || []).map((f) => ({
                    type: 'Feature',
                    geometry: f.geometry,
                    properties: { route_type: item.route_type ?? 1 },
                }))
            )
            .filter((f) => f.geometry?.type === 'LineString' || f.geometry?.type === 'MultiLineString')

        // Nothing selected — clear any stale mask and bail out
        if (!selectedFeatures.length) {
            commit('setRouteMaskGeometry', null)
            commit('setRouteMaskError', null)
            return
        }

        commit('setRouteMaskLoading', true)
        commit('setRouteMaskGeometry', null)  // clear stale mask immediately so UI doesn't show old overlay
        commit('setRouteMaskError', null)

        try {
            const response = await fetch(ROUTE_MASK_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: rootState.token,
                },
                body: JSON.stringify({ type: 'FeatureCollection', features: selectedFeatures }),
                signal: maskAbortController.signal,
            })

            if (!response.ok) throw new Error(`HTTP ${response.status}`)

            const data = await response.json()
            commit('setRouteMaskGeometry', data)
        } catch (err) {
            // AbortError is expected when a newer request supersedes this one — ignore silently
            if (err.name !== 'AbortError') {
                commit('setRouteMaskError', err.message || 'failed')
            }
        } finally {
            // Only clear the loading state if no newer request has superseded this one.
            // Without this guard, an aborted call's finally would stomp the loading=true
            // set by the newer request, making the spinner vanish while the new fetch
            // is still in-flight (visible during Lambda cold starts).
            if (maskAbortController === thisController) {
                commit('setRouteMaskLoading', false)
            }
        }
    },
    toggleGeometrySelection({ commit, dispatch }, payload) {
        commit("setGeometrySelection", payload)
        dispatch("updateSelectedGeometry")
    },
    setGeometryRouteType({ commit, dispatch }, { index, value }) {
        commit("setGeometryRouteType", { index, value })
        dispatch("updateSelectedGeometry")
    },
    setAllGeometriesRouteType({ commit, dispatch }, value) {
        commit("setAllGeometriesRouteType", value)
        dispatch("updateSelectedGeometry")
    },
    toggleAllGeometries({ commit, dispatch, getters }) {
        const nextState = !getters.allSelected
        commit("setAllGeometriesSelection", nextState)
        dispatch("updateSelectedGeometry")
    },
    clearGeometry({ commit }) {
        commit("setSelectedGeometry", null)
        commit("setLoadedGeometries", [])
        commit("setSelectedContract", null)
        commit("setSelectedOrder", null)
        commit("setGeometrySource", null)
        commit("setShowValidation", false)
        commit("setRouteMaskGeometry", null)
        commit("setRouteMaskError", null)
    },
    async startOptimization({ state, getters, commit, rootState, dispatch }, showAlert) {
        if (!getters.canOptimize) {
            return 'invalid'
        }

        const maxDurationSeconds = (state.maxDurationHours || 0) * 3600 + (state.maxDurationMinutes || 0) * 60
        const includeVehicles = maxDurationSeconds === 0

        // Build layer features: each selected loadedGeometry becomes a Feature whose geometry
        // is a FeatureCollection, preserving layer name and route_type. The `selected` property
        // is stripped from individual features — it is UI-only state.
        const layerFeatures = state.loadedGeometries
            .filter((item) => item.selected)
            .map((item) => {
                const cleanFeatures = (item.geometry?.features || []).map((feature) => {
                    const { selected: _, ...cleanProps } = feature.properties || {}
                    return {
                        type: 'Feature',
                        geometry: feature.geometry,
                        properties: cleanProps,
                    }
                })
                return {
                    type: 'Feature',
                    geometry: {
                        type: 'FeatureCollection',
                        features: cleanFeatures,
                    },
                    properties: {
                        name: item.name || item.description || item.display_name || null,
                        route_type: item.route_type ?? DEFAULT_ROUTE_TYPE,
                    },
                }
            })

        // Build vehicle features
        const vehicleFeatures = []
        if (maxDurationSeconds > 0 && state.singleVehicle) {
            const cleanVehicle = {
                ...state.singleVehicle,
                properties: {
                    ...state.singleVehicle.properties,
                    max_duration: maxDurationSeconds,
                },
            }
            delete cleanVehicle.properties.locationManuallySet
            vehicleFeatures.push(cleanVehicle)
        } else if (includeVehicles) {
            state.vehicles.forEach((vehicle) => {
                const cleanVehicle = {
                    ...vehicle,
                    properties: { ...vehicle.properties },
                }
                delete cleanVehicle.properties.locationManuallySet
                vehicleFeatures.push(cleanVehicle)
            })
        }

        const cleanedVehicleFeatures = vehicleFeatures.map(v => {
            const props = { ...v.properties }
            if (state.operationType === 'road_inspection') {
                delete props.work_width
                delete props.work_speed
                delete props.include_return_trip
            }
            return { ...v, properties: props }
        })

        const payload = {
            type: 'FeatureCollection',
            features: [...layerFeatures, ...cleanedVehicleFeatures],
            operation_type: state.operationType,
        }

        const requestBody = { data: payload }
        if (state.optimizationName) requestBody.name = state.optimizationName
        if (!state.resubmitJobId) requestBody.operation_type = state.operationType
        if (state.resubmitJobId) requestBody.jobId = state.resubmitJobId

        // Store used vehicles for simulation legend — use singleVehicle array in max duration mode
        const usedVehiclesForLegend = maxDurationSeconds > 0 && state.singleVehicle
            ? [state.singleVehicle]
            : state.vehicles
        commit("storeUsedVehicles", usedVehiclesForLegend)
        commit("setOptimizerLoading", true)

        try {
            const submitResponse = await fetch(ROUTE_OPTIMIZATION_JOB_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: rootState.token,
                },
                body: JSON.stringify(requestBody),
            })

            const { jobId } = await submitResponse.json()
            if (!submitResponse.ok || !jobId) {
                showAlert?.()
                commit("storeUsedVehicles", [])
                commit("setOptimizerLoading", false)
                return false
            }

            // Start background job
            jobService.startJob(
                jobId,
                rootState.token,
                (resultData) => dispatch('processOptimizationResult', resultData),
                (error) => {
                    // Store the failed job ID so the user can retry from the same setup
                    // session without creating a new job each time.
                    commit('setResubmitJobId', jobId)
                    showAlert?.('job_failed')
                    commit("storeUsedVehicles", [])
                    commit("setOptimizerLoading", false)
                }
            )

            return { jobId }
        } catch (err) {
            showAlert?.()
            commit("storeUsedVehicles", [])
            commit("setOptimizerLoading", false)
            return false
        }
    },
    processOptimizationResult({ commit, state }, resultData) {
        const VEHICLE_COLORS = [
            "#7FC200", "#2196F3", "#FF8C00", "#FF6B6B", "#4ECDC4",
            "#45B7D1", "#FFA07A", "#98D8C8", "#F7DC6F"
        ]

        const simArray = []
        resultData.forEach((obj, vehicleIndex) => {
            obj.metadata.vehicleColor = VEHICLE_COLORS[vehicleIndex % VEHICLE_COLORS.length]
            obj.geojson.features.forEach((feature) => {
                feature.properties.traversal_type =
                    feature.properties.priority > -1 ? "work" : "resettle"
            })
            simArray.push(obj)
        })

        commit("storeRouteOptimizationResult", simArray)
        commit("setOptimizerLoading", false)

        if (state.loadedGeometries.length > 0) {
            const allFeatures = state.loadedGeometries
                .filter((g) => g.selected)
                .flatMap((item) => item.geometry?.features || [])
            if (allFeatures.length > 0) {
                commit("setOriginalRouteGeometry", { type: "FeatureCollection", features: allFeatures })
            }
        }
    },
    cancelJobs() {
        jobService.cancelAllJobs()
    },
    resetSetupState({ commit, dispatch }) {
        dispatch("cancelJobs")
        commit("resetRouteOptimization")
    },
    loadSetupFromRequestData({ commit, dispatch }, { requestData, name, jobId, operationType }) {
        if (!requestData?.features) return

        // Separate layer features (geometry is FeatureCollection) from vehicle Point features.
        // The new payload format wraps each layer as a Feature { geometry: FeatureCollection, properties: { name, route_type } }.
        // For backward compatibility with old flat payloads, fall back to grouping all non-Point
        // line features into a single anonymous layer.
        const layerFeatures = requestData.features.filter(
            (f) => f.geometry?.type === 'FeatureCollection'
        )
        const vehicleFeatures = requestData.features.filter(
            (f) => f.geometry?.type === 'Point' && f.properties?.type === 'vehicle'
        )

        let items = []

        if (layerFeatures.length > 0) {
            // New layered format — restore each layer with its original name and route_type
            items = layerFeatures.map((layerFeature, index) => {
                const layerGeometry = layerFeature.geometry
                const hasValidGeometry = layerGeometry?.features?.some(
                    (f) => f.geometry?.type === 'LineString' || f.geometry?.type === 'MultiLineString'
                )
                const routeType = layerFeature.properties?.route_type ?? DEFAULT_ROUTE_TYPE
                return {
                    id: `rerun-${index}`,
                    name: layerFeature.properties?.name || name || `Layer ${index + 1}`,
                    geometry: layerGeometry,
                    selected: hasValidGeometry,
                    disabled: !hasValidGeometry,
                    route_type: routeType,
                    color: ROUTE_TYPE_COLORS[routeType] || ROUTE_TYPE_COLORS[DEFAULT_ROUTE_TYPE],
                    line_width: 2,
                }
            })
        } else {
            // Backward compatibility: old flat payload — collect all non-Point line features
            // into one anonymous layer
            const lineFeatures = requestData.features.filter(
                (f) => f.geometry?.type === 'LineString' || f.geometry?.type === 'MultiLineString'
            )
            if (lineFeatures.length > 0) {
                const routeType = lineFeatures[0].properties?.route_type ?? DEFAULT_ROUTE_TYPE
                items = [{
                    id: 'rerun-0',
                    name: name || 'Route',
                    geometry: { type: 'FeatureCollection', features: lineFeatures },
                    selected: true,
                    disabled: false,
                    route_type: routeType,
                    color: ROUTE_TYPE_COLORS[routeType] || ROUTE_TYPE_COLORS[DEFAULT_ROUTE_TYPE],
                    line_width: 2,
                }]
            }
        }

        commit('setLoadedGeometries', items)
        commit('setGeometrySource', 'file')
        commit('setOptimizationName', name || null)
        commit('setResubmitJobId', jobId || null)
        commit('setOperationType', requestData.operation_type || operationType || 'winter_maintenance')

        // Always keep originalRouteGeometry in sync so the simulation view
        // can show the original route overlay regardless of which path was taken
        // (direct run, re-run, copy, or view from jobs list).
        const allFeatures = items
            .filter((g) => g.selected)
            .flatMap((item) => item.geometry?.features || [])
        commit('setOriginalRouteGeometry', allFeatures.length > 0
            ? { type: 'FeatureCollection', features: allFeatures }
            : null
        )

        if (vehicleFeatures.length > 0) {
            const firstVehicle = vehicleFeatures[0]
            const maxDuration = firstVehicle.properties?.max_duration || 0

            if (maxDuration > 0) {
                const hours = Math.floor(maxDuration / 3600)
                const minutes = Math.floor((maxDuration % 3600) / 60)
                commit('setMaxDurationHours', hours >= 0 ? hours : null)
                commit('setMaxDurationMinutes', minutes >= 0 ? minutes : null)
                commit('setSingleVehicle', firstVehicle)
            } else {
                commit('setMaxDurationHours', null)
                commit('setMaxDurationMinutes', null)
                commit('setVehicles', vehicleFeatures)
            }
        }

        // Trigger mask fetch now that all geometry and vehicle state is committed
        dispatch('updateSelectedGeometry')
    },
    async copyFromJob({ dispatch, commit }, { requestData, name, copyLabel, operationType }) {
        await dispatch('resetSetupState')
        await dispatch('loadSetupFromRequestData', { requestData, name: name ? `${name} (${copyLabel})` : null, jobId: null, operationType })
        commit('setResubmitJobId', null)
    },
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
}
